import * as Sentry from '@sentry/browser'
import * as types from 'src/store/mutation-types'
import { Cookies, LocalStorage, Quasar } from 'quasar'
import { TeamClient } from 'src/api/Teams'
import User from 'src/models/user'
import { UserClient } from 'src/api/Users'
import { Validator } from 'vee-validate/dist/vee-validate.minimal.esm.js'
import auth from 'src/auth'
import axios from 'axios'
import i18n from 'src/i18n/i18n'

const USER_COOKIE_NAME = '_lk_usr_id'

// initial state
// shape: [{ id, quantity }]
const state = {
  authenticated: false,
  user: null,
  locale: null,
  activeTeamId: null,
  token: '',
  credentials: null
}

// getters
const getters = {
  authenticated: state => state.authenticated,
  user: state => state.user,
  locale: state => state.locale,
  activeTeam: (state, getters, rootState) => rootState.team.teams.find(team => team.id === state.activeTeamId),
  token: state => state.token,
  credentials: state => state.credentials
}

const actions = {
  login ({ commit }, credentials) {
    return auth.login(credentials)
      .then(({ token, user, teams, team, role }) => {
        commit(types.SET_TOKEN, token)
        commit(types.SET_USER, user)
        commit(types.SET_LOCALE, user.settings.language)
        commit(types.SET_TEAMS, teams)
        commit(types.SET_ACTIVE_TEAM, team)
        commit(types.SET_ROLE, role)
        commit(types.SET_MATOMO_USER_ID, user)
        commit(types.SET_AUTHENTICATED, true)
      })
  },

  refreshToken ({ commit }) {
    return auth.refresh()
      .then(({ token }) => {
        commit(types.SET_TOKEN, token)
      })
  },

  check ({ dispatch, commit, state }) {
    return new Promise((resolve, reject) => {
      // Is there already a token in local store
      if (LocalStorage.has('token') || Cookies.has('token')) {
        const token = LocalStorage.get.item('token') || Cookies.get('token')
        commit(types.SET_TOKEN, token)
        Cookies.remove('token')

        // Is user data already loaded
        // If not load data and hydrate store
        if (!state.user) {
          dispatch('refreshUserData')
            .then(resolve)
            .catch(() => {
              commit(types.LOGOUT)
              reject(new Error('Failed to fetch user data'))
            })
        } else {
          resolve()
        }
      } else {
        resolve()
      }
    })
  },

  refreshUserData ({ commit }) {
    return new Promise((resolve, reject) => {
      auth.fetchUserData()
        .then(({ user, teams, team, role }) => {
          commit(types.SET_USER, user)
          commit(types.SET_LOCALE, user.settings.language)
          commit(types.SET_TEAMS, teams)
          commit(types.SET_ACTIVE_TEAM, team)
          commit(types.SET_ROLE, role)
          commit(types.SET_MATOMO_USER_ID, user)
          commit(types.SET_AUTHENTICATED, true)

          Sentry.configureScope((scope) => {
            scope.setUser({ email: user.email })
          })

          resolve()
        })
        // User data could not be fetched
        .catch(error => {
          commit(types.LOGOUT)

          reject(error)
        })
    })
  },

  refreshTeamData ({ commit, getters }) {
    const teamId = getters.activeTeam.id

    new TeamClient()
      .include([
        'settings',
        'settings.default_email_address',
        'settings.default_address',
        'settings.default_phone_number',
        'settings.default_bank_details',
        'members',
        'open_invites'
      ])
      .get(teamId)
      .then((response) => {
        const team = response.data.data[0]
        commit(types.UPDATE_TEAM, team)
      })
  },

  parseTeamFromRoute ({ dispatch, commit, state }, route, next) {
    return new Promise((resolve, reject) => {
      let teamId = parseInt(route.params.teamId)

      if (teamId && teamId !== state.activeTeamId) {
        dispatch('changeTeam', teamId)
          .then(team => resolve(team))
          .catch(error => {
            reject(error)
          })
      } else {
        resolve()
      }
    })
  },

  changeTeam ({ commit, rootState }, teamId) {
    return new Promise((resolve, reject) => {
      let newTeam = rootState.team.teams.find(team => team.id === teamId)

      if (newTeam) {
        commit(types.SET_ACTIVE_TEAM, newTeam)
        commit(types.SET_ROLE, newTeam.role_id)

        resolve(newTeam)
      } else {
        reject(new Error('Could not change team'))
      }
    })
  },

  logout ({ commit }) {
    commit(types.LOGOUT)
  },

  updateUser ({ commit, state }, userData) {
    return new UserClient()
      .include(['teams', 'teams.settings', 'settings'])
      .update(userData)
      .then(response => {
        let user = new User(response.data.data)
        let role = state.user.role

        commit(types.SET_USER, user)
        commit(types.SET_LOCALE, user.settings.language)
        commit(types.SET_ROLE, role)
      })
  },

  uploadAvatar ({ commit }, avatar) {
    return new UserClient()
      .uploadAvatar(avatar)
      .then(response => {
        let avatarUrl = response.data.data.avatar_url
        commit(types.SET_USER_AVATAR, avatarUrl)
      })
  },

  removeAvatar ({ commit }) {
    return new UserClient()
      .deleteAvatar()
      .then(() => commit(types.SET_USER_AVATAR, null))
  },

  saveCredentials ({ commit }, credentials) {
    commit(types.SET_CREDENTIALS, credentials)
  },

  deleteCredentials ({ commit }) {
    commit(types.SET_CREDENTIALS, null)
  },

  set2FAState ({ commit }, status) {
    commit(types.SET_2FA, status)
  }
}

// mutations
const mutations = {
  [types.SET_TOKEN] (state, token) {
    state.token = token
    LocalStorage.set('token', token)
    axios.defaults.headers.common['Authorization'] = 'Bearer ' + token
  },

  [types.SET_USER] (state, user) {
    state.user = user
    state.authenticated = true
    Cookies.set(USER_COOKIE_NAME, user.email, { expires: 10, path: '/', domain: 'likvi.de', secure: true })
  },

  [types.SET_LOCALE] (state, locale) {
    state.locale = locale
    LocalStorage.set('locale', locale)

    // Set language for Quasar components
    // We import the language here so we don't have to laod all languages right from the start.
    let quasarLang = locale === 'en' ? 'en-us' : locale // Quasar expects en-us or en-uk so we fallback to en-us
    import(`quasar-framework/i18n/${quasarLang}`).then(lang => {
      Quasar.i18n.set(lang.default)
    })

    // Set language for vue-i18n
    i18n.locale = locale

    // Set language for vee-validate
    Validator.localize(locale)
  },

  [types.SET_ACTIVE_TEAM] (state, team) {
    state.activeTeamId = team ? team.id : null
  },

  [types.SET_ROLE] (state, roleId) {
    state.user.role = roleId
  },

  [types.SET_AUTHENTICATED] (state, value) {
    state.authenticated = value
  },

  [types.SET_USER_AVATAR] (state, avatarUrl) {
    state.user.settings.avatar_url = avatarUrl
    state.user.avatar_url = avatarUrl
  },

  [types.LOGOUT] (state) {
    LocalStorage.remove('token')
    Cookies.remove('token')
    Cookies.remove(USER_COOKIE_NAME)
    state.token = ''
    state.user = null
    state.authenticated = false

    // Log out for Matomo
    window._paq.push(['resetUserId'])
    window._paq.push(['trackPageView'])
  },

  [types.SET_CREDENTIALS] (state, credentials) {
    state.credentials = credentials
  },

  [types.SET_2FA] (state, status) {
    state.user.two_factor_enabled = status
  },

  [types.SET_MATOMO_USER_ID] (state, user) {
    window._paq.push(['setUserId', user.email])
    window._paq.push(['trackPageView'])
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
